import * as React from 'react';
import { cn } from '@/lib/utils';
import { Slot } from '@radix-ui/react-slot';
import { FiLogOut, FiMenu, FiX, FiChevronDown } from 'react-icons/fi';
import { useSession } from '@/hooks/useSession';
import { useNavigate, NavLink, Link } from 'react-router-dom';
import { MenuItem as MenuItemDefault } from './menu-item';
import { studentMenu } from './menu-student';
import { adminMenu } from './menu-admin';
import { MenuItemHelp } from './menu-item-help';
import { useCheckIncludeProfiles } from '@/hooks/useAuthorityLevel';
import { AvatarWrapper } from '@/components/ui/avatar';
import { checkPermission } from '@/hooks/usePermission';
import { usePlatformAbilities } from '@/hooks/usePlatformAbilities';
import './menu.sass';

const MenuWrapper = React.forwardRef(({ className, hasCustomLogos, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-menu-wrapper',
      'text-white',
      // Mobile styles (max-width: 783px)
      'max-md:fixed max-md:left-0 max-md:right-0 max-md:top-0 max-md:z-[105] max-md:h-[46px]',
      // Desktop styles (min-width: 768px)
      'md:flex md:flex-col md:gap-[clamp(12px,5.5vh,44px)]',
      'z-[999] md:fixed md:left-0 md:top-0 md:h-screen md:w-16 md:bg-primary',
      'md:!px-3 md:py-[clamp(12px,3vh,24px)]',
      hasCustomLogos && 'c-menu-wrapper--has-custom-logos max-md:top-[68px]', // Ajuste para logo customizada
      className
    )}
    {...props}
  />
));
MenuWrapper.displayName = 'MenuWrapper';

const MenuHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-menu-header',
      // Mobile styles
      'max-md:z-[107] max-md:h-[46px] max-md:bg-primary-lighter max-md:shadow-dropshadow',
      'max-md:relative max-md:flex max-md:items-center max-md:justify-between max-md:gap-4 max-md:px-4',
      // Desktop styles
      'md:flex md:px-0',
      className
    )}
    {...props}
  />
));
MenuHeader.displayName = 'MenuHeader';

const MenuNav = React.forwardRef(({ className, isExpanded, hasCustomLogos, ...props }, ref) => (
  <nav
    ref={ref}
    className={cn(
      'c-menu-nav',
      // Desktop
      'md:flex md:h-0 md:w-10 md:flex-grow md:flex-col md:justify-between',
      // Mobile - Base
      'max-md:fixed max-md:inset-y-[46px] max-md:left-0 max-md:right-0 max-md:z-[106]',
      'max-md:overflow-y-auto max-md:bg-primary max-md:p-4',
      'max-md:flex max-md:flex-col max-md:justify-between max-md:gap-4',
      'max-md:translate-y-[-100%] max-md:transform',
      'max-md:transition-transform max-md:duration-200 max-md:ease-out',
      // Mobile - Offset para Custom Logos (Ajustando a si mesmo)
      hasCustomLogos &&
        cn(
          // O nav começa abaixo do header (68px do logo customizado + 46px do header do menu)
          'max-md:top-[calc(68px+46px)]',
          // A altura é ajustada de acordo com o novo 'top'
          'max-md:h-[calc(100dvh-68px-46px)]'
        ),
      // Mobile - Expanded State
      isExpanded && 'max-md:translate-y-0',
      className
    )}
    {...props}
  />
));
MenuNav.displayName = 'MenuNav';

const MenuFavicon = React.forwardRef(({ className, src, ...props }, ref) => (
  <img
    ref={ref}
    src={src}
    alt="Logotipo"
    className={cn(
      'c-menu-favicon',
      // Mobile size
      'max-md:h-8 max-md:w-8',
      // Desktop size
      'md:mx-auto md:h-7 md:w-7',
      'cursor-pointer',
      className
    )}
    {...props}
  />
));
MenuFavicon.displayName = 'MenuFavicon';

const MenuToggleButton = React.forwardRef(({ className, isExpanded, ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    className={cn(
      'c-menu-toggle-btn',
      // Shared button reset
      'cursor-pointer appearance-none p-0',
      'inline-flex items-center justify-center',
      'h-9 w-9', // 36px
      'rounded-full',
      'bg-transparent',
      'transition-colors duration-150',
      // Mobile styles
      'max-md:text-primary-dark',
      'max-md:hover:bg-primary-lightest max-md:focus:bg-primary-lightest',
      // Desktop styles (hidden)
      'md:hidden',
      className
    )}
    {...props}
  >
    {/* Renderiza o ícone apropriado com o tamanho correto */}
    {React.createElement(isExpanded ? FiX : FiMenu, { className: 'w-5 h-5' })}
  </button>
));
MenuToggleButton.displayName = 'MenuToggleButton';

const MenuList = React.forwardRef(({ className, isNoGap, ...props }, ref) => (
  <ul
    ref={ref}
    className={cn(
      'c-menu-list',
      'm-0 list-none p-0',
      'flex flex-col',
      // Gap condicional
      isNoGap ? 'gap-0' : 'gap-[clamp(0px,2vh,16px)]',
      className
    )}
    {...props}
  />
));
MenuList.displayName = 'MenuList';

const MenuItem = React.forwardRef(({ className, hasSubmenu, isExpanded, ...props }, ref) => (
  <li
    ref={ref}
    className={cn(
      'c-menu-item menu__item',
      'group/item',
      // Variants
      hasSubmenu && 'menu__item--has-submenu group/has-submenu has-submenu',
      isExpanded &&
        cn(
          'group/is-expanded is-expanded',
          // Mobile styles when expanded
          'max-md:text-primary',
          'max-md:bg-white',
          'max-md:rounded-[20px]'
        ),
      className
    )}
    {...props}
  />
));
MenuItem.displayName = 'MenuItem';

const MenuLink = React.forwardRef(({ className, isAvatar, isActive, to, onClick, ...props }, ref) => {
  const Comp = to ? NavLink : Slot;

  return (
    <Comp
      ref={ref}
      to={to}
      onClick={onClick}
      className={cn(
        'menu__link',
        // Estado Ativo Padrão
        'aria-[current=page]:bg-primary-light',
        // ANULAÇÃO DO ESTILO ATIVO QUANDO O ITEM PAI ESTÁ EXPANDIDO (Mobile)
        // Usa o seletor arbitrário no link para anular o background ativo quando o ancestral tem a classe 'is-expanded'
        'max-md:group-[.is-expanded]:aria-[current=page]:bg-transparent',
        // Variants
        isAvatar && 'menu__link--avatar',
        isActive && 'menu__link--is-active',
        className
      )}
      {...props}
    />
  );
});
MenuLink.displayName = 'MenuLink';

const MenuIcon = React.forwardRef(({ className, ...props }, ref) => (
  <Slot
    ref={ref}
    className={cn(
      'c-menu-icon',
      'w-4', // 16px
      'flex-shrink-0',
      // stroke-width: 2 é mantido pelo ícone SVG/componente (e.g., FiLogOut)
      className
    )}
    {...props}
  />
));
MenuIcon.displayName = 'MenuIcon';

const MenuLabel = React.forwardRef(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn(
      'c-menu-label',
      // Desktop styles
      'md:text-sm md:font-light md:opacity-0',
      'md:group-hover/item:opacity-100',
      'md:[.has-submenu_&]:hidden',
      className
    )}
    {...props}
  />
));
MenuLabel.displayName = 'MenuLabel';

const MenuArrow = React.forwardRef(({ isExpanded, className, ...props }, ref) => (
  <FiChevronDown
    ref={ref}
    className={cn(
      'c-menu-arrow',
      'w-4 transition-transform duration-200',
      'md:group/has-submenu:hidden',
      isExpanded && 'rotate-180',
      // Desktop styles
      'md:hidden',
      className
    )}
    {...props}
  />
));
MenuArrow.displayName = 'MenuArrow';

const Menu = React.forwardRef(({ className, hasCustomLogos, ...props }, ref) => {
  const navigate = useNavigate();
  const { session, signOut } = useSession();
  const { abilities } = usePlatformAbilities();
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [screenHeight, setScreenHeight] = React.useState(window.innerHeight);

  const appName = process.env.REACT_APP_NAME;
  const isStudent = useCheckIncludeProfiles({ only: ['student'] });

  let favicon;

  switch (appName) {
    case 'canaleducacao':
      favicon = '/themes/canaleducacao/favicon/seduc-favicon.png';
      break;
    case 'maiseducacao':
      favicon = '/themes/maiseducacao/favicon/seduc-favicon.png';
      break;
    case 'unisescoop':
    case 'demo':
    case 'encceja':
    case 'siape':
    case 'egepi':
    case 'vegalabs':
    case 'inkluziva':
    case 'seducpi':
    case 'tjpara':
    case 'pit':
    case 'caboverde':
    case 'oros':
    case 'sett-solutions':
    case 'aprovavp':
    case 'floriano-educa-7':
      favicon = `/themes/${appName}/favicon/favicon-menu-32x32.png`;
      break;

    case 'infantil':
      // Placeholder: reusa o favicon do floriano-educa-7 até existir /themes/infantil/.
      favicon = '/themes/floriano-educa-7/favicon/favicon-menu-32x32.png';
      break;

    default:
      favicon = '/themes/demo/favicon/favicon-menu-32x32.png';
      throw new Error('No REACT_APP_NAME set.');
  }

  const menu = isStudent ? studentMenu({ session, abilities, checkPermission }) : adminMenu({ session, abilities, checkPermission });

  React.useEffect(() => {
    const handleResize = () => setScreenHeight(window.innerHeight);

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <MenuWrapper
      ref={ref}
      hasCustomLogos={hasCustomLogos}
      className={className}
      {...props}
    >
      <MenuHeader>
        <MenuFavicon
          src={favicon}
          onClick={() => {
            navigate('/');
            setIsExpanded(false);
          }}
        />
        <MenuToggleButton
          isExpanded={isExpanded}
          onClick={() => setIsExpanded(!isExpanded)}
        />
      </MenuHeader>

      <MenuNav
        isExpanded={isExpanded}
        hasCustomLogos={hasCustomLogos}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <MenuList isNoGap={screenHeight < 625}>
          {menu.map(item => (
            <MenuItemDefault
              item={item}
              key={item.id}
            />
          ))}
        </MenuList>

        <MenuList isNoGap={screenHeight < 625}>
          <MenuItem>
            <MenuLink
              isAvatar
              to="/profile"
            >
              <AvatarWrapper
                src={session?.user?.image?.url}
                text={session?.user?.name}
                className="[&>span]:cursor-pointer"
              />
              <MenuLabel>{session?.user?.name}</MenuLabel>
            </MenuLink>
          </MenuItem>
          <MenuItemHelp />
          <MenuItem>
            <MenuLink>
              <a onClick={() => signOut()}>
                <MenuIcon>
                  <FiLogOut />
                </MenuIcon>{' '}
                <MenuLabel>Sair</MenuLabel>
              </a>
            </MenuLink>
          </MenuItem>
        </MenuList>
      </MenuNav>
    </MenuWrapper>
  );
});

export { Menu, MenuIcon, MenuList, MenuItem, MenuLink, MenuNav, MenuHeader, MenuWrapper, MenuFavicon, MenuToggleButton, MenuLabel, MenuArrow };
